import { ConnectedProps, connect } from 'react-redux'
import { SellInvoiceModelType } from '@teinor/erp/types/company/order/sell/sellInvoice'
import { Modify } from '@teinor/erp'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import { OrderData } from '../../../../inputTableLayouts/order/OrderLayout/types'
import { returnClassOfSpanTemplate } from '../../../../../utils/globals/tailwind'
import withLoading from '../../../../../utils/hoc/withLoading'
import { ComponentTabObject } from '../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../utils/reducers/company/employees'
import { WorkingCenterPayload } from '../../../../../utils/reducers/company/workingCenters'
import { SellDeliveryNoteModelType } from '@teinor/erp/types/company/order/sell/sellDeliveryNote'
import AppStyleDecider from '../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import { useOrderITHeaderHandleChangeLogic } from '../../../../inputTableLayouts/order/useOrderITHeaderHandleChange'

type MultiOrderRefundITHeaderProps = Modify<InputTableLeftColumnCompleteRequiredProps, ReduxMultiOrderRefundITHeader & {
    completeData: OrderData | null
    translateText: {
        titleTab: string
    }
    urlNavigate: string
}>

const MultiOrderRefundITHeader: React.FC<MultiOrderRefundITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={MultiOrderRefundITHeaderDefault} NewComponent={MultiOrderRefundITHeaderNew} {...rest} />
}

const MultiOrderRefundITHeaderDefault: React.FC<MultiOrderRefundITHeaderProps> = ({ workingCenters, translateText, urlNavigate, ...rest }) => {
    const tabs: ComponentTabObject = {
        'main': {
            component: MultiOrderRefundITHeaderMain,
            text: translateText.titleTab,
            extraProps: { workingCenters }
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const MultiOrderRefundITHeaderNew: React.FC<MultiOrderRefundITHeaderProps> = ({ workingCenters, headerData, getCompleteData, setCompleteData, updateDataURL }) => {
    const { t } = useTwinTranslation()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData, updateDataURL })
    const sections: FormRender<SellInvoiceModelType | SellDeliveryNoteModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData || {}} />
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('workingCenter', 'Centro de trabajo')} value={workingCenters?.[headerData?.WorkingCenterId]?.name || ''} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} />,
                },
            ]
        }
    ]
    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
    )
}

interface MultiOrderRefundITHeaderMainProps extends InputTableLeftTabsProps {
    workingCenters: WorkingCenterPayload
}

const MultiOrderRefundITHeaderMain: React.FC<MultiOrderRefundITHeaderMainProps> = ({ workingCenters,  handleChange, headerData}) => {
    const { t } = useTwinTranslation()
    const sections: FormRender<SellInvoiceModelType | SellDeliveryNoteModelType > = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} />
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('workingCenter', 'Centro de trabajo')} value={workingCenters?.[headerData?.WorkingCenterId]?.name || ''} className={returnClassOfSpanTemplate(3)} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} className={returnClassOfSpanTemplate(2)} />,
                },
            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData}/>
    )
}

const MultiOrderRefundITHeaderDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
}

export type ReduxMultiOrderRefundITHeader = ConnectedProps<typeof multiOrderRefundITHeaderConnect>
const mapMultiOrderRefundITHeaderConnector = (state: AllReduxPayloads) => ({ employees: state.employees, workingCenters: state.workingCenters})
const multiOrderRefundITHeaderConnect = connect(mapMultiOrderRefundITHeaderConnector, MultiOrderRefundITHeaderDispatch)
const MultiOrderRefundITHeaderConnectLoading = withLoading(MultiOrderRefundITHeader, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }, { fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }])
const MultiOrderRefundITHeaderConnect = multiOrderRefundITHeaderConnect(MultiOrderRefundITHeaderConnectLoading)

export default MultiOrderRefundITHeaderConnect